<template>
  <div v-show="open" :class="$style.container" @click="handleCloseLightbox">
    <button :class="$style.closeButton">
      <NsIcon :class="$style.closeIcon" icon-name="icon-close" size="custom" />
      {{ escLabel.substring(0, 3) }}
    </button>
    <div :class="$style.contentContainer" @click.stop>
      <div :class="$style.content">
        <video
          v-if="videoSrcTags"
          ref="lightboxVideo"
          :class="$style.video"
          controls
          v-html="videoSrcTags"
        />
        <video v-else ref="lightboxVideo" :class="$style.video" controls>
          <source :src="videoSrc" />
        </video>
      </div>
    </div>
  </div>
</template>

<script>
import { NsIcon } from "@nuskin/design-components";
/**
 * This component is a lightbox that will auto-start playing a video and pause
 * it when closed
 *
 * @event close Fired when the user signals the lightbox should to be closed.
 */
export default {
  name: "NsLightbox",
  components: { NsIcon },
  props: {
    /**
     * The abbreviated label for the escape button. Limit is 3 chars
     */
    escLabel: {
      type: String,
      default: ""
    },
    /**
     * The open state of the lightbox
     */
    open: Boolean,
    /**
     * The video source url
     */
    videoSrc: {
      type: String,
      default: ""
    },
    /**
     * Source tags can be used instead of ctaUrl
     */
    videoSrcTags: {
      type: String,
      default: ""
    }
  },
  watch: {
    open(value) {
      if (value) {
        window.addEventListener("keydown", this.handleKeydown);
        this.$refs.lightboxVideo.play();
      } else {
        window.removeEventListener("keydown", this.handleKeydown);
        this.$refs.lightboxVideo.pause();
      }
    },
    videoSrc() {
      this.$refs.lightboxVideo.load();
    }
  },
  methods: {
    /**
     * Closes lightbox if Escape key is pressed
     * Toggles play/pause if spacebar is pressed
     */
    handleKeydown(event) {
      // Closes lightbox if Escape key is pressed
      if (event.key === "Escape") {
        this.handleCloseLightbox();
      }

      // Toggles play/pause if spacebar is pressed
      if (event.key === " ") {
        this.toggleVideo();
      }
    },
    /**
     * Closes lightbox
     */
    handleCloseLightbox() {
      this.$emit("close");
    },
    /**
     * Toggles play and pause
     */
    toggleVideo() {
      if (this.$refs.lightboxVideo.paused) {
        this.$refs.lightboxVideo.play();
      } else {
        this.$refs.lightboxVideo.pause();
      }
    }
  }
};
</script>

<style lang="scss" module>
.container {
  position: fixed;
  top: 0px;
  bottom: 0px;
  left: 0px;
  right: 0px;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 999;
}

.closeButton {
  position: absolute;
  top: 20px;
  right: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 60px;
  height: 60px;
  border: none;
  border-radius: 30px;
  color: white;
  background-color: black;
  opacity: 0.6;
  cursor: pointer;
  z-index: 1;

  &:hover {
    color: #000;
    background-color: #f5f5f5;
    opacity: 0.43;
  }

  // nested for specificity
  .closeIcon {
    width: 24px;
    height: 24px;
  }
}

.contentContainer {
  padding-bottom: 47.8125%;
  width: 100%;
  height: 0;
  margin: 0 auto;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  overflow: hidden;
}

.content {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

.video {
  width: 100%;
  height: 100%;

  &:focus {
    outline: none;
  }
}

@media (min-width: 1024px) {
  .contentContainer {
    width: 85%;
  }
}
</style>
